import {csvTool} from './csv';
import {xlsxTool} from './xlsx';

export enum FileType {
	CSV = 'text/csv',
	XLS = 'application/vnd.ms-excel',
	XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	PDF = 'application/pdf',
	JPEG = 'image/jpeg',
	PNG = 'image/png',
	DOC = '.doc',
	DOCX = '.docx',
}

export type ReadMethod = 'readAsArrayBuffer' | 'readAsBinaryString' | 'readAsDataURL' | 'readAsText';

const defaultReadMethods: Partial<Record<FileType, ReadMethod>> = {
	[FileType.CSV]: 'readAsText',
	[FileType.XLS]: 'readAsBinaryString',
	[FileType.XLSX]: 'readAsBinaryString',
};

export const readFile = (file: File, readMethod?: ReadMethod): Promise<FileReader> => {
	return new Promise((res, rej) => {
		const fileType: FileType = file.type as FileType;
		const reader = new FileReader();
		const method: ReadMethod = readMethod
			? readMethod
			: Object.hasOwn(defaultReadMethods, fileType)
			? defaultReadMethods[fileType] || 'readAsText'
			: 'readAsText';

		reader.onload = async ({target}: ProgressEvent<FileReader>) => {
			if (!target) rej('no file reader found');
			else res(target);
		};

		reader[method](file);
	});
};

export enum Measurement {
	BYTE = 'b',
	KB = 'kb',
	MB = 'mb',
	GB = 'gb',
	TB = 'tb',
}

export const convertToHumanReadable = (
	value: number,
	measurement: Measurement = Measurement.BYTE,
): [number, Measurement] => {
	const nextMeasurement = Object.values(Measurement).at(Object.values(Measurement).indexOf(measurement) + 1);

	if (!nextMeasurement) return [value, measurement];

	const result = value / 1024;

	if (result > 1024) return convertToHumanReadable(result, nextMeasurement);

	return [result, nextMeasurement];
};

export function readHeaders(file: File, data: string | ArrayBuffer | null): string[] {
	if (file.type === FileType.CSV) return csvTool.readHeaders(data);

	if (file.type === FileType.XLS || file.type === FileType.XLSX) return xlsxTool.readHeaders(data);

	return [];
}
